import type { CrateType } from '@/features/order/model/orderDetails.types'
import { Box, Divider, Group, Space, Text } from '@mantine/core'
import { IconCircleCheck, IconCircleX } from '@tabler/icons-react'

const EMPTY_MISSING_CRATE_TYPES: string[] = []

interface OrderCrateScanRowsProps {
  crateTypes: CrateType[]
  showScanProgress?: boolean
  enableMissingMarking?: boolean
  missingCrateTypes?: string[]
  crateItemTypeClassName: string
  crateItemCountClassName: string
  crateItemGroupClassName?: string
  rowClassName?: string
}

export function OrderCrateScanRows({
  crateTypes,
  showScanProgress = false,
  enableMissingMarking = false,
  missingCrateTypes = EMPTY_MISSING_CRATE_TYPES,
  crateItemTypeClassName,
  crateItemCountClassName,
  crateItemGroupClassName,
  rowClassName,
}: OrderCrateScanRowsProps) {
  return crateTypes.map((crate, index) => {
    const scanned = crate.scannedCount ?? 0
    const isMissing = missingCrateTypes.includes(crate.type)
    const isComplete = showScanProgress && !isMissing && crate.count > 0 && scanned >= crate.count
    const countDisplay = showScanProgress ? `${scanned}/${crate.count}` : String(crate.count)
    const trailing = crate.icon
      ? <Text span>{crate.icon}</Text>
      : <Space />

    const rowContent = !showScanProgress ? (
      <Group grow justify="space-between" className={rowClassName}>
        <Text span className={crateItemTypeClassName}>{crate.type}</Text>
        <Text span className={crateItemCountClassName}>{countDisplay}</Text>
        {trailing}
      </Group>
    ) : isMissing ? (
      <Box
        style={{
          border: '1.5px solid var(--mantine-color-red-6)',
          borderRadius: 8,
          padding: '8px 12px',
        }}
      >
        <Group grow justify="space-between" className={rowClassName}>
          <Group gap="xs" align="center" wrap="nowrap">
            <IconCircleX size={24} color="var(--mantine-color-red-6)" style={{ flexShrink: 0 }} />
            <Text span fw={700} className={crateItemTypeClassName}>{crate.type}</Text>
          </Group>
          <Text span fw={700} className={crateItemCountClassName}>{countDisplay}</Text>
          {trailing}
        </Group>
      </Box>
    ) : isComplete ? (
      <Box
        style={{
          border: '1.5px solid var(--mantine-color-green-6)',
          borderRadius: 8,
          padding: '8px 12px',
        }}
      >
        <Group grow justify="space-between" className={rowClassName}>
          <Group gap="xs" align="center" wrap="nowrap">
            <IconCircleCheck size={24} color="var(--mantine-color-green-6)" style={{ flexShrink: 0 }} />
            <Text span fw={700} className={crateItemTypeClassName}>{crate.type}</Text>
          </Group>
          <Text span fw={700} className={crateItemCountClassName}>{countDisplay}</Text>
          {trailing}
        </Group>
      </Box>
    ) : (
      <Group grow justify="space-between" className={rowClassName}>
        <Text
          span
          className={crateItemTypeClassName}
          style={enableMissingMarking ? { textDecoration: 'underline' } : undefined}
        >
          {crate.type}
        </Text>
        <Text span className={crateItemCountClassName}>{countDisplay}</Text>
        {trailing}
      </Group>
    )

    return (
      <Box
        key={crate.type}
        className={crateItemGroupClassName}
        data-crate-row-state={
          !showScanProgress
            ? 'default'
            : isComplete
              ? 'complete'
              : isMissing
                ? 'missing'
                : 'partial'
        }
      >
        {/* Spotlight only the row — exclude the dashed Divider so Step 7 does not bleed into the next crate. */}
        <Box data-onboarding={crate.type === 'Bulk Ambient' ? 'tutorial-bulk-ambient-row' : undefined}>
          {rowContent}
        </Box>
        {index < crateTypes.length - 1 && <Divider variant="dashed" my="sm" />}
      </Box>
    )
  })
}
